import React, { useState, useMemo } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Box,
  Typography,
  Chip,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Collapse,
  Divider,
  Badge,
} from '@mui/material';
import {
  FlashOn as FlashOnIcon,
  Delete as DeleteIcon,
  AccessTime as AccessTimeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Apps as AppsIcon,
  Devices as DevicesIcon,
  Code as CodeIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { ApplicationObservatoryEvent } from '../hooks/useApplicationObservatory';

interface ActivityPanelProps {
  events: ApplicationObservatoryEvent[];
  onClearEvents: () => void;
  compact?: boolean;
}

type GroupingMode = 'application' | 'client' | 'function' | 'none';

export const ActivityPanel: React.FC<ActivityPanelProps> = ({
  events,
  onClearEvents,
  compact = false,
}) => {
  const [groupingMode, setGroupingMode] = useState<GroupingMode>('application');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Group events
  const groupedEvents = useMemo(() => {
    if (groupingMode === 'none') {
      return { ungrouped: events };
    }

    const groups: Record<string, ApplicationObservatoryEvent[]> = {};
    
    events.forEach(event => {
      let groupKey: string;
      
      switch (groupingMode) {
        case 'application':
          groupKey = event.data.applicationKey || event.data.applicationId || 'unknown-app';
          break;
        case 'client':
          groupKey = event.data.clientId || event.data.clientName || 'unknown-client';
          break;
        case 'function':
          groupKey = event.data.functionName || event.data.operationId || 'unknown-function';
          break;
        default:
          groupKey = 'ungrouped';
      }
      
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(event);
    });

    return groups;
  }, [events, groupingMode]);

  const toggleGroup = (groupKey: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedGroups(newExpanded);
  };

  const getEventIcon = (eventType: string) => {
    switch (eventType) {
      case 'request:started':
        return '🚀';
      case 'request:completed':
        return '✅';
      case 'request:failed':
        return '❌';
      case 'function:called':
        return '⚙️';
      case 'error:occurred':
        return '🔴';
      case 'client:connected':
        return '🔌';
      case 'client:disconnected':
        return '⏹️';
      case 'application:deployed':
        return '📦';
      case 'application:updated':
        return '🔄';
      default:
        return '📊';
    }
  };

  const getEventColor = (eventType: string): 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' | 'default' => {
    switch (eventType) {
      case 'request:completed':
        return 'success';
      case 'request:failed':
      case 'error:occurred':
        return 'error';
      case 'request:started':
        return 'info';
      case 'function:called':
        return 'primary';
      case 'client:connected':
        return 'success';
      case 'client:disconnected':
        return 'warning';
      case 'application:deployed':
      case 'application:updated':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();

    if (diff < 1000) return 'just now';
    if (diff < 60000) return `${Math.floor(diff / 1000)}s ago`;
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    return date.toLocaleTimeString();
  };

  const getEventSummary = (event: ApplicationObservatoryEvent) => {
    const app = event.data.applicationKey || '';
    const client = event.data.clientName || event.data.clientId?.substring(0, 8) || '';
    const func = event.data.functionName || '';
    const duration = event.data.duration ? `(${event.data.duration}ms)` : '';
    
    switch (event.type) {
      case 'request:started':
        return `${client} → ${app}/${func} started`;
      case 'request:completed':
        return `${client} → ${app}/${func} completed ${duration}`;
      case 'request:failed':
        return `${client} → ${app}/${func} failed: ${event.data.error || 'Unknown error'}`;
      case 'function:called':
        return `${func}() called ${event.data.success ? '✓' : '✗'} ${duration}`;
      case 'error:occurred':
        return `Error in ${app}: ${event.data.message || event.data.error}`;
      case 'client:connected':
        return `${client} connected to ${app}`;
      case 'client:disconnected':
        return `${client} disconnected`;
      case 'application:deployed':
        return `${app} deployed to ${event.data.environment || 'unknown'}`;
      case 'application:updated':
        return `${app} configuration updated`;
      default:
        return JSON.stringify(event.data).substring(0, 100);
    }
  };

  const renderEventItem = (event: ApplicationObservatoryEvent, index: number) => (
    <ListItem 
      key={`${event.timestamp}-${index}`} 
      sx={{ 
        pl: groupingMode !== 'none' ? 4 : 2,
        py: 1,
        borderLeft: 2,
        borderColor: event.type.includes('failed') || event.type.includes('error') 
          ? 'error.main' 
          : event.type.includes('completed') 
          ? 'success.main' 
          : 'primary.main',
      }}
    >
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <span style={{ fontSize: '1.2em' }}>{getEventIcon(event.type)}</span>
            <Chip 
              label={event.type.split(':').join(' ')} 
              color={getEventColor(event.type)} 
              size="small" 
            />
            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTimeIcon sx={{ fontSize: 14 }} />
              {formatTimestamp(event.timestamp)}
            </Typography>
          </Box>
        }
        secondary={
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {getEventSummary(event)}
          </Typography>
        }
      />
    </ListItem>
  );

  const renderGroupHeader = (groupKey: string, groupEvents: ApplicationObservatoryEvent[]) => {
    const isExpanded = expandedGroups.has(groupKey);
    
    const successCount = groupEvents.filter(e => 
      e.type === 'request:completed' || (e.type === 'function:called' && e.data.success)
    ).length;
    const errorCount = groupEvents.filter(e => 
      e.type.includes('failed') || e.type.includes('error')
    ).length;

    const getGroupIcon = () => {
      switch (groupingMode) {
        case 'application':
          return <AppsIcon />;
        case 'client':
          return <DevicesIcon />;
        case 'function':
          return <CodeIcon />;
        default:
          return <FlashOnIcon />;
      }
    };
    
    return (
      <ListItem
        button
        onClick={() => toggleGroup(groupKey)}
        sx={{
          bgcolor: 'background.default',
          borderLeft: 3,
          borderColor: errorCount > 0 ? 'error.main' : 'primary.main',
          mb: 0.5,
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
      >
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {getGroupIcon()}
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {groupKey}
              </Typography>
              <Badge badgeContent={groupEvents.length} color="primary" sx={{ ml: 1 }} />
              {successCount > 0 && (
                <Chip 
                  icon={<CheckCircleIcon />} 
                  label={successCount} 
                  size="small" 
                  color="success" 
                  variant="outlined" 
                />
              )}
              {errorCount > 0 && (
                <Chip 
                  icon={<ErrorIcon />} 
                  label={errorCount} 
                  size="small" 
                  color="error" 
                  variant="outlined" 
                />
              )}
              <Box sx={{ flex: 1 }} />
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
          }
          secondary={
            <Typography variant="caption" color="text.secondary">
              Last activity: {formatTimestamp(groupEvents[0].timestamp)}
            </Typography>
          }
        />
      </ListItem>
    );
  };

  return (
    <Card>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FlashOnIcon />
            <Typography variant="h6">Activity Stream</Typography>
          </Box>
        }
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip label={`${events.length} events`} color="primary" />
            <IconButton onClick={onClearEvents} size="small">
              <DeleteIcon />
            </IconButton>
          </Box>
        }
      />
      <CardContent>
        {/* Grouping Controls */}
        {!compact && (
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
            <ToggleButtonGroup
              value={groupingMode}
              exclusive
              onChange={(e, newMode) => newMode && setGroupingMode(newMode)}
              size="small"
            >
              <ToggleButton value="application">
                <AppsIcon sx={{ mr: 0.5, fontSize: 16 }} />
                By App
              </ToggleButton>
              <ToggleButton value="client">
                <DevicesIcon sx={{ mr: 0.5, fontSize: 16 }} />
                By Client
              </ToggleButton>
              <ToggleButton value="function">
                <CodeIcon sx={{ mr: 0.5, fontSize: 16 }} />
                By Function
              </ToggleButton>
              <ToggleButton value="none">
                <FlashOnIcon sx={{ mr: 0.5, fontSize: 16 }} />
                Flat
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />

        {events.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <Typography color="text.secondary">
              No events yet. Activity will appear here in real-time.
            </Typography>
          </Box>
        ) : groupingMode === 'none' ? (
          // Flat list view
          <List sx={{ maxHeight: compact ? 300 : 600, overflow: 'auto' }}>
            {events.map((event, index) => renderEventItem(event, index))}
          </List>
        ) : (
          // Grouped view
          <List sx={{ maxHeight: compact ? 300 : 600, overflow: 'auto' }}>
            {Object.entries(groupedEvents).map(([groupKey, groupEvents]) => (
              <React.Fragment key={groupKey}>
                {renderGroupHeader(groupKey, groupEvents)}
                <Collapse in={expandedGroups.has(groupKey)} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {groupEvents.map((event, index) => renderEventItem(event, index))}
                  </List>
                </Collapse>
              </React.Fragment>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
};

export default ActivityPanel;

